// Count sessions (kitchen flow, Stage 3): count one place at a time, in shelf
// order. Mounted as Store Inventory → "Count" (any place) and as Production →
// "Count the kitchen" (kitchens only). No money anywhere on this screen.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient, { ApiClientError } from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { qtyAfterScan } from '@/lib/scanToLine'
import { getStoredUserRole } from '@/lib/staff-roles'
import { CT } from '@/lib/inventoryFlow/countStrings'
import {
  filterLines, groupByShelf, loadCountDrafts, pendingCounts, progress, settleDrafts, shownBook, shownCount, shownVariance,
  storeCountDrafts, withCountedAge,
} from '@/lib/inventoryFlow/countLogic'
import type { CountSession, CountSessionLine } from '@/lib/inventoryFlow/visibilityTypes'
import type { FlowLocation } from '@/lib/inventoryFlow/types'
import { cn } from '@/lib/utils'
import { LoadError } from './LoadError'

type Notify = (kind: 'success' | 'error', message: string) => void

const fmt = (n: number) => String(Math.round(n * 1000) / 1000)
const newKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)

function errText(e: unknown): string {
  if (e instanceof ApiClientError && e.code && CT.errors[e.code]) return CT.errors[e.code]
  return friendlyError(e)
}

export function CountSheet({ mode, notify }: { mode: 'store' | 'kitchen'; notify: Notify }) {
  const role = getStoredUserRole()
  const canApprove = role === 'admin' || role === 'inventory_manager'
  const { data: locRes } = useQuery({ queryKey: ['inventory-flow', 'locations'], queryFn: () => apiClient.getFlowLocations() })
  const places = useMemo(
    () => (locRes?.data ?? []).filter((l: FlowLocation) => l.is_active && l.kind !== 'transit' && (mode === 'store' || l.kind === 'kitchen')),
    [locRes, mode],
  )
  const [placeId, setPlaceId] = useState<string | null>(null)
  useEffect(() => {
    if (placeId || places.length === 0) return
    const preferred = mode === 'kitchen' ? places.find((p) => p.is_sale_source) : places.find((p) => p.is_receiving_default)
    setPlaceId((preferred ?? places[0]).id)
  }, [places, placeId, mode])

  // Counts sent for approval anywhere, so the store sees which place is waiting.
  const { data: waitingRes } = useQuery({
    queryKey: ['inventory-flow', 'count-sessions', 'submitted'],
    queryFn: () => apiClient.getCountSessions({ status: 'submitted', limit: 50 }),
    enabled: canApprove,
  })
  const waitingAt = new Set((waitingRes?.data ?? []).map((s) => s.location.id))
  const { data: listRes, refetch: refetchList, isError: listError, isLoading: listLoading, isFetching: listFetching } = useQuery({
    queryKey: ['inventory-flow', 'count-sessions', placeId],
    queryFn: () => apiClient.getCountSessions({ location_id: placeId!, limit: 20 }),
    enabled: !!placeId,
  })
  const sessions = listRes?.data ?? []
  const active = sessions.find((s) => s.status === 'open' || s.status === 'submitted') ?? null

  if (locRes && places.length === 0) return <p className="text-muted-foreground">{CT.noKitchen}</p>

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">{CT.title}</h2>
        <p className="text-muted-foreground">{CT.subtitle}</p>
      </div>
      {places.length > 1 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{CT.where}</p>
          <div className="flex flex-wrap gap-2">
            {places.map((p) => (
              <button key={p.id} type="button" onClick={() => setPlaceId(p.id)}
                className={cn('h-10 rounded-lg border px-4 text-sm font-semibold touch-manipulation',
                  placeId === p.id ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}>
                {p.name}
                {waitingAt.has(p.id) && <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs text-white">{CT.waitingBadge}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {placeId && listLoading && <p className="text-muted-foreground">…</p>}
      {/* A failed load must not offer "Start a count" over one that is open. */}
      {placeId && listError && !listRes && <LoadError busy={listFetching} onRetry={() => void refetchList()} />}
      {placeId && listRes && (active
        ? <ActiveCount key={active.id} id={active.id} canApprove={canApprove} notify={notify} onDone={() => void refetchList()} />
        : <StartCount placeId={placeId} notify={notify} onStarted={() => void refetchList()} />)}
      <PastCounts sessions={sessions.filter((s) => s.status === 'approved' || s.status === 'cancelled')} />
    </div>
  )
}

function StartCount({ placeId, notify, onStarted }: { placeId: string; notify: Notify; onStarted: () => void }) {
  const [all, setAll] = useState(false)
  const [busy, setBusy] = useState(false)
  const start = async () => {
    setBusy(true)
    try {
      await apiClient.createCountSession({ location_id: placeId, all_items: all }, newKey())
      onStarted()
    } catch (e) {
      notify('error', errText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="h-4 w-4" checked={all} onChange={(e) => setAll(e.target.checked)} />
        {CT.everyItem}
      </label>
      <Button disabled={busy} onClick={() => void start()}>{CT.start}</Button>
    </div>
  )
}

const AUTOSAVE_MS = 1200

function ActiveCount({ id, canApprove, notify, onDone }: { id: string; canApprove: boolean; notify: Notify; onDone: () => void }) {
  const qc = useQueryClient()
  const role = getStoredUserRole()
  const { data, refetch, isError: sessionError, isFetching: sessionFetching } = useQuery({ queryKey: ['inventory-flow', 'count-session', id], queryFn: () => apiClient.getCountSession(id) })
  const session: CountSession | undefined = data?.data
  const lines: CountSessionLine[] = session?.lines ?? []
  // Unsaved counts live on the device too (a refresh or a sleeping tablet
  // keeps them); each line autosaves shortly after it is typed and on blur.
  const stored = useMemo(() => loadCountDrafts(id), [id])
  const [drafts, setDrafts] = useState<Record<string, number | null>>(stored.drafts)
  const enteredAt = useRef<Record<string, number>>(stored.enteredAt)
  const scannedRef = useRef<Set<string>>(new Set())
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [confirming, setConfirming] = useState<'approve' | 'cancel' | null>(null)
  const [note, setNote] = useState('')
  const open = session?.status === 'open'
  const submitted = session?.status === 'submitted'
  const bookHidden = session?.book_hidden ?? true

  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const linesRef = useRef(lines)
  linesRef.current = lines
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlight = useRef<Promise<boolean> | null>(null)

  useEffect(() => {
    storeCountDrafts(id, { drafts, enteredAt: enteredAt.current })
  }, [id, drafts])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const pending = pendingCounts(lines, drafts)
  const p = progress(lines, drafts)

  const refresh = async () => {
    await refetch()
    void qc.invalidateQueries({ queryKey: ['inventory-flow', 'count-sessions'] })
  }

  // Sends every unsaved line (one save at a time; a later call waits for the
  // one in flight, then sends what is still pending).
  const flush = async (): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    while (inFlight.current) await inFlight.current
    const body = withCountedAge(pendingCounts(linesRef.current, draftsRef.current), enteredAt.current, Date.now())
    if (body.length === 0) return true
    const run = (async () => {
      setSaving(true)
      try {
        const res = await apiClient.saveCountLines(id, body, newKey())
        // The saved sheet comes back with the save: show it, then drop the
        // drafts it now holds (one typed again meanwhile stays).
        qc.setQueryData(['inventory-flow', 'count-session', id], res)
        setDrafts((d) => settleDrafts(d, body))
        setSaveFailed(false)
        void qc.invalidateQueries({ queryKey: ['inventory-flow', 'count-sessions'] })
        return true
      } catch (e) {
        setSaveFailed(true)
        if (!(e instanceof ApiClientError) || !e.code) return false
        notify('error', errText(e))
        return false
      } finally {
        setSaving(false)
      }
    })()
    inFlight.current = run
    try {
      return await run
    } finally {
      inFlight.current = null
    }
  }

  const scheduleSave = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => void flush(), AUTOSAVE_MS)
  }

  // Counts kept on the device from before a refresh go up once the sheet
  // loads; a failed save retries every few seconds and when the wifi is back.
  const resumed = useRef(false)
  useEffect(() => {
    if (!open || resumed.current) return
    resumed.current = true
    if (pendingCounts(linesRef.current, draftsRef.current).length > 0) scheduleSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    if (!saveFailed) return
    const retry = () => void flush()
    const t = setInterval(retry, 15_000)
    window.addEventListener('online', retry)
    return () => {
      clearInterval(t)
      window.removeEventListener('online', retry)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFailed])

  const setDraft = (itemId: string, v: number | null) => {
    enteredAt.current = { ...enteredAt.current, [itemId]: Date.now() }
    setDrafts((d) => ({ ...d, [itemId]: v }))
    scheduleSave()
  }

  const scanner = useItemScanner({
    enabled: open,
    onItem: (match) => {
      // Read and update the refs, not this render's state: a second code can
      // arrive before the screen redraws, and must count on from the first.
      const line = linesRef.current.find((l) => l.stock_item_id === match.item.id)
      const first = !scannedRef.current.has(match.item.id)
      const latest = draftsRef.current
      const current = line ? shownCount(line, latest) ?? 0 : latest[match.item.id] ?? 0
      const next = qtyAfterScan(current, match.pack_qty, first)
      scannedRef.current.add(match.item.id)
      draftsRef.current = { ...latest, [match.item.id]: next }
      setDraft(match.item.id, next)
      if (!line) {
        // Not on the sheet yet: saving adds it at the end.
        void flush()
        return { ok: true, text: CT.scanNew(match.item.name) }
      }
      return { ok: true, text: CT.scanAdded(match.item.name, fmt(next), line.unit) }
    },
  })

  const act = async (action: 'submit' | 'approve' | 'cancel' | 'reopen') => {
    setConfirming(null)
    if (action === 'submit' && !(await flush())) {
      notify('error', CT.notSavedYet)
      return
    }
    setBusy(true)
    try {
      await apiClient.countSessionAction(id, action, newKey(), action === 'approve' && note.trim() ? { note: note.trim() } : undefined)
      notify('success', CT.done[action])
      if (action === 'cancel') storeCountDrafts(id, { drafts: {}, enteredAt: {} })
      if (action === 'approve') {
        for (const k of ['stockItems', 'stockSummary', 'stockMovements', 'advancedStockReport', 'inventoryActivity']) {
          void qc.invalidateQueries({ queryKey: [k] })
        }
        void qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      }
      await refresh()
      onDone()
    } catch (e) {
      notify('error', errText(e))
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  if (!session) return sessionError ? <LoadError busy={sessionFetching} onRetry={() => void refetch()} /> : <p className="text-muted-foreground">…</p>
  const groups = groupByShelf(filterLines(lines, q))
  const saveState = saving ? CT.saving : saveFailed || pending.length > 0 ? CT.unsaved(pending.length) : p.counted > 0 ? CT.allSaved : null

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold">{session.session_no} · {session.location.name}</p>
          <p className="text-sm text-muted-foreground">
            {CT.status[session.status]} · {CT.counted(p.counted, p.total)}
            {!bookHidden && session.lines_with_variance != null ? ` · ${CT.differences(p.differing)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {open && <Button disabled={busy || p.counted === 0} onClick={() => void act('submit')}>{CT.submit}</Button>}
          {submitted && canApprove && (
            <Button disabled={busy} onClick={() => setConfirming('approve')}>{CT.approve}</Button>
          )}
          {submitted && role !== 'manager' && (
            <Button variant="outline" disabled={busy} onClick={() => void act('reopen')}>{CT.reopen}</Button>
          )}
          <Button variant="ghost" className="px-4" disabled={busy} onClick={() => setConfirming('cancel')}>{CT.cancel}</Button>
        </div>
      </div>
      {confirming && (
        <div role="alertdialog" className="space-y-3 rounded-lg border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/40">
          <p className="text-sm font-medium">
            {confirming === 'approve' ? CT.approveConfirm(session.lines_with_variance ?? p.differing) : submitted ? CT.withdrawConfirm : CT.cancelConfirm}
          </p>
          {confirming === 'approve' && canApprove && (
            <Input placeholder={CT.approveNote} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirming(null)}>{CT.back}</Button>
            <Button disabled={busy} onClick={() => void act(confirming)}>{CT.yes}</Button>
          </div>
        </div>
      )}
      {open && saveState && (
        <p className={cn('text-sm', saveFailed ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
          {saveFailed ? CT.saveRetry : saveState}
        </p>
      )}
      {open && <p className="text-sm text-muted-foreground">{CT.blind}</p>}
      {submitted && <p className="text-sm text-muted-foreground">{bookHidden ? CT.waitingApproval : CT.approverHint}</p>}
      {scanner.ui}
      <Input placeholder={CT.search} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.category} className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{g.category}</h3>
            <ul className="divide-y rounded-lg border">
              {g.lines.map((l) => (
                <CountRow key={l.stock_item_id} line={l} drafts={drafts} editable={open} bookHidden={bookHidden}
                  onChange={(v) => setDraft(l.stock_item_id, v)} onBlur={() => void flush()} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function CountRow({ line, drafts, editable, bookHidden, onChange, onBlur }: {
  line: CountSessionLine
  drafts: Record<string, number | null>
  editable: boolean
  bookHidden: boolean
  onChange: (v: number | null) => void
  onBlur: () => void
}) {
  const shown = shownCount(line, drafts)
  const variance = bookHidden ? null : shownVariance(line, drafts)
  const book = bookHidden ? null : shownBook(line, drafts)
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 p-3">
      <div className="min-w-[12rem] flex-1">
        <p className="text-sm font-medium">{line.item_name}</p>
        {book != null && <p className="text-sm text-muted-foreground">{CT.book(fmt(book), line.unit)}</p>}
      </div>
      <div className="flex items-center gap-2">
        {variance != null && (
          <span className={cn('rounded-md px-2 py-1 text-sm font-medium',
            variance === 0 ? 'bg-success-surface text-success-fg' : variance < 0 ? 'bg-danger-surface text-danger-fg' : 'bg-warning-surface text-warning-fg')}>
            {variance === 0 ? CT.matches : variance < 0 ? CT.less(fmt(-variance), line.unit) : CT.more(fmt(variance), line.unit)}
          </span>
        )}
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          disabled={!editable}
          aria-label={line.item_name}
          placeholder={CT.notCounted}
          className="w-32 text-center font-semibold"
          value={shown == null ? '' : String(shown)}
          onBlur={onBlur}
          onChange={(e) => {
            const raw = e.target.value.trim()
            if (raw === '') return onChange(null)
            const n = Number(raw)
            if (Number.isFinite(n) && n >= 0) onChange(n)
          }}
        />
        <span className="min-w-[2.5rem] text-sm text-muted-foreground">{line.unit}</span>
      </div>
    </li>
  )
}

function PastCounts({ sessions }: { sessions: CountSession[] }) {
  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold">{CT.history}</h3>
      {sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{CT.noHistory}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {sessions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
              <span className="font-medium">{s.session_no}</span>
              <span className="text-muted-foreground">
                {CT.status[s.status]}
                {s.approved_by_name ? ` ${CT.by} ${s.approved_by_name}` : ''}
                {s.approved_at ? ` · ${new Date(s.approved_at).toLocaleString()}` : ''}
              </span>
              <span className="text-muted-foreground">
                {CT.counted(s.lines_counted, s.lines_total)}
                {s.lines_with_variance != null ? ` · ${CT.differences(s.lines_with_variance)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
